// The chrome's half of the view (PLAN-CLIENT-SPLIT day 6).
//
// **The models decide and this translates.** `hotbarModel`, `selectionModel`,
// `handModel` and the rest are the rules' numbers and the
// session's verbs; the contract says "pawn" where they say 1 and hands the
// client an `OptionId` where they hold a command. This is the one place that
// is written down, so a zone drawn from `SessionView` holds no rule and no
// number it could get wrong.
//
// **Every part is memoised on what it reads**, the way each zone memoised its
// own model: the hotbar sweeps every foundry against every neighbour, and the
// session's revision moves on every click.
//
// **An option is only good for the view that minted it.** `choose` looks the
// id up in the parts as they stand now, so a control from a board that has
// moved on is refused rather than run against the wrong square.

import type {
  ArmyReadout,
  Badge,
  CarryPoint,
  Control,
  ControlCard,
  GroundCard,
  HandView,
  Holding,
  HotbarView,
  MaterialId,
  InspectorSection,
  InspectorView,
  ObjectivesView,
  OptionId,
  Outcome,
  Overlays,
  PriceRow,
  ReadoutsView,
  ShellContext,
  ThingCard,
  TurnRowView
} from "src/api";
import {
  ARMY_NAME,
  asSide,
  asSquare,
  DIRECTION_NAME,
  LINE_NAME,
  PIECE_NAME,
  STRUCTURE_NAME,
  BUILDING_NAME,
  VARIANT_NAME
} from "src/engine/api/names";
import { hasPower, POWERS } from "src/engine/rules/campaign/powers";
import type { Command, GameState } from "src/engine/rules/commands";
import { mintableFor } from "src/engine/rules/industry/machines";
import { GRADE_SLOTS, type StructureKind } from "src/engine/rules/industry/state";
import { STRUCTURE_NAMES } from "src/engine/rules/industry/structures";
import {
  type MaterialLine,
  materialId,
  materialName
} from "src/engine/rules/materials";
import { livingSides, pieceAt } from "src/engine/rules/position";
import { PIECE_NAMES, pieceVariant, RULES } from "src/engine/rules/rules";
import { totalScore } from "src/engine/rules/tally";
import { type Color, type Direction, type KingKind, pieceColor, WHITE } from "src/shared/board";
import type { selectionVerbs } from "./actions";
import type { armingVerbs } from "./arming";
import { armyName } from "./armyName";
import type { SessionCore } from "./core";
import { economyFor } from "./economy";
import { PIECE_GLYPH, PIECE_ICON, STRUCTURE_GLYPH } from "./glyphs";
import { type GroundModel, groundModel, type UnderRow, underRow } from "./ground";
import { handModel, trophyModel } from "./hand";
import { healthBars } from "./healthBars";
import { type BuildTile, type ForgeTile, hotbarModel, type TileRow } from "./hotbar";
import { objectivesModel } from "./objectives";
import { armyPieces, stockProse } from "./readouts";
import { REFUSAL, UNDO_REFUSAL } from "./refusals";
import {
  type ContextSection,
  NOTE_HINT,
  type SelectionCard,
  type SelectionItem,
  selectionModel,
  type SelectionRun
} from "./selection";
import { slotName } from "./shelfTotals";
import { turnRowModel } from "./turnRow";

/** Everything the chrome draws. `SessionView` is the board's half and this. */
export interface ChromeView {
  hotbar: HotbarView;
  inspector: InspectorView;
  hand: HandView;
  objectives: ObjectivesView;
  readouts: ReadoutsView;
  turnRow: TurnRowView;
  overlays: Overlays;
}

/** The verbs an option runs. The session's own, handed in so there is one of each. */
export interface ChromeVerbs {
  arming: ReturnType<typeof armingVerbs>;
  selection: ReturnType<typeof selectionVerbs>;
  carrying: {
    carryForge(tile: ForgeTile, at: CarryPoint): void;
    carryBuild(tile: BuildTile, at: CarryPoint): void;
    carryToken(kind: StructureKind, at: CarryPoint): void;
  };
  setMintFacing(facing: Direction | null): void;
}

type Run = () => void;
type Lift = (at: CarryPoint) => void;

/** A view, what each of its controls does, and what dragging one does. */
interface Part<V> {
  view: V;
  options: Map<OptionId, Run>;
  /** Only the hotbar mints these: a tile picked up rather than clicked. */
  lifts: Map<OptionId, Lift>;
}

/** One part, rebuilt only when something it reads has changed identity. */
class Memo<V> {
  private keys: readonly unknown[] | null = null;
  private part: Part<V> | null = null;

  get(
    keys: readonly unknown[],
    build: (options: Map<OptionId, Run>, lifts: Map<OptionId, Lift>) => V
  ): Part<V> {
    const was = this.keys;
    if (this.part && was && was.length === keys.length && keys.every((key, i) => key === was[i])) {
      return this.part;
    }
    const options = new Map<OptionId, Run>();
    const lifts = new Map<OptionId, Lift>();
    this.part = { view: build(options, lifts), options, lifts };
    this.keys = keys;
    return this.part;
  }
}

// --- the rules' numbers in the contract's names -------------------------------

const lineOf = (slot: number): MaterialLine => Math.floor(slot / GRADE_SLOTS) as MaterialLine;

const holding = (slot: number, count: number): Holding => ({
  material: materialId(lineOf(slot), slot % GRADE_SLOTS),
  line: LINE_NAME[lineOf(slot)],
  grade: slot % GRADE_SLOTS,
  count,
  name: slotName(slot)
});

/** A pair list, in the order it was given: the inspector's is dearest first. */
const holdings = (rows: readonly { slot: number; count: number }[]): Holding[] =>
  rows.map((row) => holding(row.slot, row.count));

/** A count per slot, as the rows that are not nought, cheapest first. */
const tally = (counts: readonly number[]): Holding[] =>
  counts.flatMap((count, slot) => (count > 0 ? [holding(slot, count)] : []));

const priceRow = (row: TileRow): PriceRow => ({
  // No bill charges fuel — it is burned, never priced — so a row is a material.
  material: materialId(row.line, row.grade) as MaterialId,
  count: row.count,
  line: LINE_NAME[row.line],
  grade: row.grade,
  name: materialName(row.line, row.grade).toLowerCase()
});

const under = (row: UnderRow | null): ThingCard["under"] =>
  row ? { text: row.text, swatch: holding(row.slot, 0) } : null;

export class Chrome {
  private shell: ShellContext = {};

  private readonly hotbar_ = new Memo<HotbarView>();
  private readonly inspector_ = new Memo<InspectorView>();
  private readonly hand_ = new Memo<HandView>();
  private readonly objectives_ = new Memo<ObjectivesView>();
  private readonly readouts_ = new Memo<ReadoutsView>();
  private readonly turnRow_ = new Memo<TurnRowView>();
  private readonly overlays_ = new Memo<Overlays>();

  constructor(
    private readonly core: SessionCore,
    private readonly verbs: ChromeVerbs
  ) {}

  /** What only the shell knows. A new object is a change; the same facts are not. */
  setShell(next: ShellContext): void {
    const was = this.shell;
    if (
      was.watching === next.watching &&
      was.machinePlays === next.machinePlays &&
      was.solo === next.solo &&
      was.profiles === next.profiles
    ) {
      return;
    }
    this.shell = next;
    this.core.changed();
  }

  /** The board on the screen: the history view's, else the live one (§29 U4). */
  private get shown(): GameState {
    const snap = this.core.snapshot;
    return snap.history.viewed ?? snap.state;
  }

  /** Whose economy the chrome is about — the run's army, or the seat's. One definition. */
  private get side(): Color {
    return this.shown.run?.side ?? this.core.localSide;
  }

  private parts(): { [K in keyof ChromeView]: Part<ChromeView[K]> } {
    return {
      hotbar: this.hotbar(),
      inspector: this.inspector(),
      hand: this.hand(),
      objectives: this.objectives(),
      readouts: this.readouts(),
      turnRow: this.turnRow(),
      overlays: this.overlays()
    };
  }

  view(): ChromeView {
    const parts = this.parts();
    return {
      hotbar: parts.hotbar.view,
      inspector: parts.inspector.view,
      hand: parts.hand.view,
      objectives: parts.objectives.view,
      readouts: parts.readouts.view,
      turnRow: parts.turnRow.view,
      overlays: parts.overlays.view
    };
  }

  /**
   * Run a control. **Refused when the view has moved on from it**, and the
   * refusal a verb says on the way is the one handed back — so the pill and
   * the caller cannot tell two stories about one click.
   */
  choose(option: OptionId): Outcome {
    let run: Run | undefined;
    for (const part of Object.values(this.parts())) {
      run = part.options.get(option);
      if (run) break;
    }
    return this.perform(run, (fn) => fn());
  }

  /** A tile dragged off the bar: armed, and carried to the square it is let go of on. */
  lift(option: OptionId, at: CarryPoint): Outcome {
    let lift: Lift | undefined;
    for (const part of Object.values(this.parts())) {
      lift = part.lifts.get(option);
      if (lift) break;
    }
    return this.perform(lift, (fn) => fn(at));
  }

  /** An option the current view did not mint is refused, out loud. */
  private perform<F>(fn: F | undefined, call: (fn: F) => void): Outcome {
    const core = this.core;
    if (!fn) {
      const refusal = "That control is not on the board any more.";
      core.setRefusal(refusal);
      return { ok: false, refusal };
    }
    const said = core.refused.said;
    call(fn);
    const now = core.refused;
    return now.said !== said && now.text !== null ? { ok: false, refusal: now.text } : { ok: true };
  }

  // --- the hotbar ---------------------------------------------------------

  private hotbar(): Part<HotbarView> {
    const { core, verbs } = this;
    const state = this.shown;
    const side = this.side;
    const keys = [state, side, core.forging, core.placing, core.mintFacing, this.shell.watching];
    return this.hotbar_.get(keys, (options, lifts) => {
      const facings: (Direction | null)[] = [null, 0, 1, 2, 3];
      const mintFacing = {
        facing: core.mintFacing === null ? null : DIRECTION_NAME[core.mintFacing],
        options: facings.map((facing) => {
          const option = `hotbar:facing:${facing === null ? "any" : DIRECTION_NAME[facing]}`;
          options.set(option, () => verbs.setMintFacing(facing));
          return { facing: facing === null ? null : DIRECTION_NAME[facing], option };
        })
      };
      // Hidden for a board nobody is playing: fixed for the length of the
      // game, so nothing flickers.
      if (this.shell.watching) {
        return { shown: false, forge: [], build: [], tokens: [], mintFacing };
      }
      const model = hotbarModel(state, side);
      return {
        shown: true,
        forge: model.forge.map((tile) => {
          const option = `hotbar:forge:${PIECE_NAME[tile.type]}`;
          options.set(option, () => verbs.arming.armForge(tile));
          lifts.set(option, (at) => verbs.carrying.carryForge(tile, at));
          return {
            option,
            piece: PIECE_NAME[tile.type],
            name: PIECE_NAMES[tile.type],
            glyph: PIECE_GLYPH[tile.type],
            ...(PIECE_ICON[tile.type] ? { icon: PIECE_ICON[tile.type] } : {}),
            caption: tile.caption,
            rows: tile.rows.map((row) => priceRow(row)),
            squares: tile.squares.map(asSquare),
            shared: [...tile.shared].map(([square, funders]) => ({ square: asSquare(square), funders })),
            forgeable: tile.forgeable,
            reason: tile.reason,
            role: tile.role,
            armed: core.forging?.type === tile.type
          };
        }),
        build: model.build.map((tile) => {
          const option = `hotbar:build:${BUILDING_NAME[tile.kind]}`;
          options.set(option, () => verbs.arming.armBuild(tile));
          lifts.set(option, (at) => verbs.carrying.carryBuild(tile, at));
          return {
            option,
            structure: STRUCTURE_NAME[tile.kind],
            building: BUILDING_NAME[tile.kind],
            name: STRUCTURE_NAMES[tile.kind],
            glyph: STRUCTURE_GLYPH[tile.kind],
            caption: tile.caption,
            rows: tile.rows.map((row) => priceRow(row)),
            squares: tile.squares.map(asSquare),
            reason: tile.reason,
            role: tile.role,
            armed: core.forging?.build === tile.kind
          };
        }),
        tokens: model.tokens.map((tile) => {
          const option = `hotbar:token:${STRUCTURE_NAME[tile.kind]}`;
          options.set(option, () => verbs.arming.beginPlace(tile.kind));
          lifts.set(option, (at) => verbs.carrying.carryToken(tile.kind, at));
          return {
            option,
            structure: STRUCTURE_NAME[tile.kind],
            name: STRUCTURE_NAMES[tile.kind],
            glyph: STRUCTURE_GLYPH[tile.kind],
            count: tile.count,
            role: tile.role,
            armed: core.placing?.kind === tile.kind
          };
        }),
        mintFacing
      };
    });
  }

  // --- the inspector ------------------------------------------------------

  private inspector(): Part<InspectorView> {
    const { core } = this;
    const state = this.shown;
    const { selected, localSide, viewing } = core;
    // **Nothing about the selection is drawn while anything is armed**: arming
    // lights squares and the next click lands on one of them.
    const armed = core.forging !== undefined || core.placing !== undefined || core.aiming !== undefined;
    // The live board's move count stands in for "what the actions read": they
    // are derived from the live state, which the shown one is unless viewing.
    const keys = [state, core.state, selected, localSide, viewing, armed, this.shell];
    return this.inspector_.get(keys, (options) => {
      if (armed || selected === undefined) return null;
      const badge = economyFor(state).detail(selected);
      if (badge === null) {
        const ground = groundModel(state, selected, localSide);
        return ground ? groundCard(selected, ground) : null;
      }
      const run = state.run;
      const powers = run ? POWERS.filter((id) => hasPower(state.industry, run.side, id)) : [];
      const model = selectionModel({
        state,
        square: selected,
        localSide,
        badge,
        actions: this.verbs.selection.actions(),
        powers,
        viewing,
        profiles: this.shell.profiles,
        machinePlays: Boolean(this.shell.machinePlays)
      });
      const control = (item: SelectionItem): Control => {
        const option = `inspector:${item.id}`;
        options.set(option, () => this.run(item.run));
        return {
          id: item.id,
          option,
          label: item.label,
          ...(item.glyph === undefined ? {} : { glyph: item.glyph }),
          ...(item.icon === undefined ? {} : { icon: item.icon }),
          ...(item.on === undefined ? {} : { on: item.on }),
          enabled: item.enabled,
          reason: item.reason,
          ...(item.card ? { card: card(item.card) } : {})
        };
      };
      const { inspector } = model;
      return {
        kind: "thing",
        at: asSquare(selected),
        name: inspector.name,
        square: inspector.square,
        owner: inspector.owner,
        pips: inspector.pips,
        detail: inspector.detail,
        stats: inspector.stats,
        holdings: holdings(inspector.holdings),
        progress: inspector.progress,
        army: inspector.army,
        note: inspector.note === null ? null : { text: inspector.note, hint: NOTE_HINT[inspector.note] ?? "" },
        under: under(underRow(state, selected)),
        controls: model.controls.map(control),
        sections: model.sections.map((section) => sectionOf(section, control)),
        ...(inspector.equipment ? { equipment: inspector.equipment } : {})
      };
    });
  }

  /** `SelectionRun` → an effect, in one place. A seventh kind is a case here and nowhere else. */
  private run(run: SelectionRun): void {
    const { core, verbs } = this;
    switch (run.kind) {
      case "act":
        verbs.selection.act(run.action, run.square);
        return;
      case "command":
        core.send(run.command);
        return;
      case "upgrade":
        verbs.arming.upgradePiece(run.square, run.target, run.variant);
        return;
      case "fuse":
        verbs.arming.fusePieces(run.parts, run.into);
        return;
      case "sacrifice":
        verbs.arming.beginSacrifice();
        return;
    }
  }

  // --- the hand -----------------------------------------------------------

  private hand(): Part<HandView> {
    const { core, verbs } = this;
    const state = this.shown;
    const aiming = core.aiming?.instance;
    const keys = [state, aiming, core.localSide, this.shell.watching];
    return this.hand_.get(keys, (options) => {
      // Never while watching: a spectator holds no seat to play a card from.
      const model = this.shell.watching ? null : handModel(state, aiming);
      if (!model) return null;
      // Unclaimed trophies (§31 A2): one card of three, or kept for score.
      const send = (command: Command): void => {
        if (!core.send(command)) core.setRefusal(REFUSAL["trophy-refused"]);
      };
      const trophies = trophyModel(state).map((trophy) => {
        const keep = `trophy:${trophy.id}:keep`;
        options.set(keep, () => send({ type: "keep-trophy", trophyId: trophy.id }));
        return {
          id: trophy.id,
          keep,
          score: RULES.trophyScore,
          offer: trophy.offer.map((card, choice) => {
            const option = `trophy:${trophy.id}:${choice}`;
            options.set(option, () => send({ type: "claim-trophy", trophyId: trophy.id, choice }));
            return { ...card, option };
          })
        };
      });
      return {
        cards: model.cards.map((held) => {
          const option = `hand:${held.instance}`;
          options.set(option, () => verbs.arming.playCard(held.instance));
          return { ...held, option };
        }),
        ...(trophies.length > 0 ? { trophies } : {})
      };
    });
  }

  // --- objectives, readouts, the turn row ---------------------------------

  private objectives(): Part<ObjectivesView> {
    const state = this.shown;
    return this.objectives_.get([state], () => objectivesModel(state));
  }

  private readouts(): Part<ReadoutsView> {
    const state = this.shown;
    const side = this.side;
    return this.readouts_.get([state, side], () => {
      const economy = economyFor(state);
      // Free Space tiles banked are the run's army's, and nobody else's.
      const readout = (army: Color): ArmyReadout => {
        const totals = economy.totals(army);
        const { position } = state;
        return {
          side: asSide(army),
          name: armyName(army),
          army: ARMY_NAME[position.kingKind[army] as KingKind],
          forges: mintableFor(position, army).map((type) => PIECE_NAME[type]),
          onClock: position.turn === army,
          stock: tally(totals.held.map((held, slot) => held + totals.stored[slot])),
          held: tally(totals.held),
          stored: tally(totals.stored),
          onBelts: tally(totals.onBelts),
          pieces: armyPieces(totals),
          belts: totals.belts,
          prose: stockProse(totals, army === state.run?.side ? (state.run?.tiles ?? 0) : 0)
        };
      };
      const run = state.run;
      return {
        mine: readout(side),
        armies: livingSides(state.position).map(readout),
        score: run ? totalScore(state) : null
      };
    });
  }

  private turnRow(): Part<TurnRowView> {
    const { core } = this;
    const state = this.shown;
    const { localSide, viewing } = core;
    // The log is what Undo reads, and the store memoises its answer on it.
    const undoWhy = core.store.undoRefusal(localSide);
    const keys = [state, core.state, localSide, viewing, undoWhy];
    return this.turnRow_.get(keys, (options) => {
      const sideToMove = state.position.turn === WHITE ? "White" : "Black";
      const model = turnRowModel({ state, localSide, sideToMove, viewing });
      options.set("turn:end", () => {
        // A click that cannot mean anything says why, here as on the button.
        if (model.endTurnRefusal) core.setRefusal(model.endTurnRefusal);
        else core.send({ type: "pass" });
      });
      return {
        terminal: model.terminal,
        status: model.status,
        sideToMove,
        turn: model.turn,
        moves: model.moves,
        stage: model.stage,
        waveOwed: model.waveOwed,
        clockOwed: model.clockOwed,
        spawnClock: model.spawnClock,
        endTurn: { option: "turn:end", refusal: model.endTurnRefusal, ready: model.endTurnReady },
        undoRefusal: undoWhy === null ? null : UNDO_REFUSAL[undoWhy]
      };
    });
  }

  // --- over the board -----------------------------------------------------

  private overlays(): Part<Overlays> {
    const state = this.shown;
    const forging = this.core.forging;
    return this.overlays_.get([state, forging], () => {
      return {
        badges: economyFor(state).badges.map(
          (badge): Badge => ({
            square: asSquare(badge.square),
            owner: asSide(badge.owner),
            kind: badge.kind,
            short: badge.short,
            title: badge.title,
            detail: badge.detail,
            pips: badge.pips,
            holdings: holdings(badge.materials),
            ...(badge.progress === undefined ? {} : { progress: badge.progress }),
            ...(badge.ready === undefined ? {} : { ready: badge.ready }),
            ...(badge.needsSetup === undefined ? {} : { needsSetup: badge.needsSetup })
          })
        ),
        health: healthBars(state).map((bar) => ({
          ...bar,
          square: asSquare(bar.square),
          owner: asSide(bar.owner)
        })),
        marks: [...(forging?.shared ?? [])].map(([square, funders]) => ({ square: asSquare(square), funders })),
        // Ascending square order, so the list is the same for the same board.
        upgraded: [...state.position.variant]
          .filter(([square]) => pieceAt(state.position, square) !== 0)
          .sort(([a], [b]) => a - b)
          .map(([square, id]) => ({
            square: asSquare(square),
            owner: asSide(pieceColor(pieceAt(state.position, square))),
            variant: VARIANT_NAME[id],
            name: pieceVariant(id).name
          }))
      };
    });
  }
}

const card = (from: SelectionCard): ControlCard => ({
  title: from.title,
  body: from.body,
  ...(from.rows ? { rows: from.rows.map((row) => priceRow(row)) } : {}),
  ...(from.state ? { state: from.state } : {})
});

function sectionOf(section: ContextSection, control: (item: SelectionItem) => Control): InspectorSection {
  switch (section.kind) {
    case "saving":
      return { ...section, options: section.options.map(control) };
    case "taps":
      return {
        ...section,
        rows: section.rows.map((row) => ({ ...row, accept: control(row.accept), offer: control(row.offer) }))
      };
    case "turret":
      return { ...section, rows: section.rows.map((row) => ({ ...control(row), stat: row.stat })) };
    case "climb":
      return { ...section, tiles: section.tiles.map((tile) => ({ ...control(tile), note: tile.note })) };
    case "sacrifice":
      return { kind: "sacrifice", item: control(section.item) };
  }
}

function groundCard(square: number, ground: GroundModel): GroundCard {
  return {
    kind: "ground",
    at: asSquare(square),
    what: ground.kind,
    name: ground.name,
    square: ground.square,
    owner: ground.owner,
    swatch: ground.slot === null ? null : holding(ground.slot, ground.count ?? 0),
    count: ground.count,
    here: ground.here,
    progress: ground.progress,
    holdings: holdings(ground.holdings),
    note: ground.note,
    under: under(ground.under)
  };
}
